import React, { forwardRef } from 'react';

export type InputType = 'text' | 'email' | 'password' | 'number' | 'date' | 'tel' | 'url';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  type?: InputType;
  hint?: string;
  error?: string;
  optional?: boolean;
}

/**
 * KERN Input Component
 *
 * Usage:
 * <Input label="Familienname" name="name" />
 * <Input label="E-Mail" type="email" error="Ungültige E-Mail" />
 * <Input label="Telefon" type="tel" optional hint="Format: +49 123 456789" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, type = 'text', hint, error, optional, id, className = '', ...props }, ref) => {
    const inputId = id || (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);
    const hintId = hint && inputId ? `${inputId}-hint` : undefined;
    const errorId = error && inputId ? `${inputId}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

    const wrapperClasses = ['kern-form-input', error && 'kern-form-input--error', className]
      .filter(Boolean)
      .join(' ');

    const inputClasses = ['kern-form-input__input', error && 'kern-form-input__input--error']
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label className="kern-label" htmlFor={inputId}>
            {label}
            {optional && <span className="kern-label__optional"> - Optional</span>}
          </label>
        )}
        {hint && (
          <div className="kern-hint" id={hintId}>
            {hint}
          </div>
        )}
        <input
          ref={ref}
          className={inputClasses}
          id={inputId}
          type={type}
          aria-describedby={describedBy}
          {...props}
        />
        {error && (
          <p className="kern-error" id={errorId} role="alert">
            <span className="kern-icon kern-icon--danger kern-icon--md" aria-hidden="true" />
            <span className="kern-body">{error}</span>
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
